import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface RemovePhotoDialogProps {
  isOpen: boolean;
  /** User confirmed — the parent fires the per-photo delete. */
  onConfirm: () => void;
  /** User cancelled or dismissed — the parent keeps every photo. */
  onCancel: () => void;
  /** The per-photo delete is in flight; disables both buttons. */
  isPending: boolean;
}

/**
 * Confirmation dialog for removing ONE photo from a synced multi-photo record.
 *
 * Presentational only — it owns no mutation. The per-photo delete (optimistic
 * cache patch, idempotent-404, 409→record-delete routing) lives in
 * [[image-viewer-modal.spec]]; this dialog just gates the confirm so a stray tap
 * on the small per-tile "×" can't silently and permanently destroy a photo for
 * everyone on the project. Mirrors [[delete-record-dialog.spec]]'s shape and
 * copy vocabulary; the lone-photo "×" routes to that record-delete dialog
 * instead, never here.
 */
export function RemovePhotoDialog({
  isOpen,
  onConfirm,
  onCancel,
  isPending,
}: RemovePhotoDialogProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={(o) => (o ? null : onCancel())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove this photo?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes this photo from the record for everyone on
            the project. The record keeps its other photos. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-12" disabled={isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="h-12 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? "Removing…" : "Remove"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
